import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Grid,
  Pagination,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Alert,
  Tab,
  Tabs,
  Avatar,
  CardHeader,
  Divider,
  LinearProgress,
  useTheme
} from "@mui/material";
import {
  Add,
  Search,
  Edit,
  Delete,
  LocalShipping,
  Star,
  Schedule,
  Business,
  Phone,
  Email,
  LocationOn,
  StarBorder,
  CheckCircle,
  Cancel,
  Warning
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import {
  getLinenVendors,
  createLinenVendor,
  updateLinenVendor,
  deleteLinenVendor,
  getServiceTypes,
  getActiveVendors,
  updateVendorStatus
} from "../../services/api";

// Tab panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vendor-tabpanel-${index}`}
      aria-labelledby={`vendor-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const LinenVendorMaster = () => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [vendors, setVendors] = useState([]);
  const [activeVendors, setActiveVendors] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingServices, setLoadingServices] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [statusFilter, setStatusFilter] = useState("active");
  const [serviceTypeFilter, setServiceTypeFilter] = useState("");
  const [pickupFilter, setPickupFilter] = useState("");

  // Available service types from backend
  const serviceTypeOptions = ["washing", "dry_cleaning", "ironing", "repair", "disinfection"];

  const initialFormState = {
    vendorCode: "",
    name: "",
    contact_person: "",
    phone: "",
    email: "",
    address: "",
    pickup_available: false,
    delivery_available: false,
    service_types: ["washing"],
    status: "active",
    rating: 3,
    turnaround_time: 24,
    capacity_per_day: 100,
    notes: ""
  };

  const [form, setForm] = useState(initialFormState);

  // Load service types
  const loadServiceTypes = useCallback(async () => {
    try {
      setLoadingServices(true);
      const res = await getServiceTypes();
      setServiceTypes(res.data?.serviceTypes || serviceTypeOptions);
    } catch (err) {
      console.error("Load service types error:", err);
      enqueueSnackbar("Failed to load service types", { variant: "error" });
      setServiceTypes(serviceTypeOptions);
    } finally {
      setLoadingServices(false);
    }
  }, [enqueueSnackbar]);

  // Load active vendors for quick reference
  const loadActiveVendors = useCallback(async () => {
    try {
      const res = await getActiveVendors();
      setActiveVendors(res.data?.vendors || []);
    } catch (err) {
      console.error("Load active vendors error:", err);
      enqueueSnackbar("Failed to load active vendors", { variant: "error" });
    }
  }, [enqueueSnackbar]);

  // Memoized load function
  const loadVendors = useCallback(async (pageNum = 1) => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: pageNum,
        limit,
        search: search.trim(),
        status: statusFilter
      };

      // Add filters if selected
      if (serviceTypeFilter) {
        params.service_type = serviceTypeFilter;
      }
      if (pickupFilter !== "") {
        params.pickup_available = pickupFilter;
      }

      const res = await getLinenVendors(params);
      const data = res.data || {};
      const vendorsList = data.vendors || [];
      const totalItems = data.pagination?.total || data.total || vendorsList.length;

      setVendors(vendorsList);
      setTotalCount(totalItems);

      const calculatedPages = data.pagination?.pages ||
        Math.max(1, Math.ceil(totalItems / limit));
      setTotalPages(calculatedPages);
      setPage(pageNum);

    } catch (err) {
      console.error("Load vendors error:", err);
      const errorMessage = err.response?.data?.message || "Failed to load vendors";
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: "error" });
      setVendors([]);
    } finally {
      setLoading(false);
    }
  }, [search, limit, statusFilter, serviceTypeFilter, pickupFilter, enqueueSnackbar]);

  // Initial load
  useEffect(() => {
    loadVendors(1);
    loadServiceTypes();
    loadActiveVendors();
  }, [loadVendors, loadServiceTypes, loadActiveVendors]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadVendors(1);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [search, loadVendors]);

  // Handle filter changes
  useEffect(() => {
    loadVendors(1);
  }, [statusFilter, serviceTypeFilter, pickupFilter, loadVendors]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenAdd = () => {
    setEditingId(null);
    setForm(initialFormState);
    setOpen(true);
  };

  const handleEdit = (vendor) => {
    setEditingId(vendor._id);
    setForm({
      vendorCode: vendor.vendorCode || "",
      name: vendor.name || "",
      contact_person: vendor.contact_person || "",
      phone: vendor.phone || "",
      email: vendor.email || "",
      address: vendor.address || "",
      pickup_available: Boolean(vendor.pickup_available),
      delivery_available: Boolean(vendor.delivery_available),
      service_types: vendor.service_types || ["washing"],
      status: vendor.status || "active",
      rating: vendor.rating || 3,
      turnaround_time: vendor.turnaround_time || 24,
      capacity_per_day: vendor.capacity_per_day || 100,
      notes: vendor.notes || ""
    });
    setOpen(true);
  };

  const handleServiceTypeChange = (serviceType) => {
    const currentServices = [...form.service_types];
    const index = currentServices.indexOf(serviceType);

    if (index === -1) {
      currentServices.push(serviceType);
    } else {
      currentServices.splice(index, 1);
    }

    setForm({ ...form, service_types: currentServices });
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      enqueueSnackbar("Vendor name is required", { variant: "warning" });
      return false;
    }
    if (form.email && !/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(form.email)) {
      enqueueSnackbar("Please enter a valid email address", { variant: "warning" });
      return false;
    }
    if (form.phone && !/^[0-9+\-\s()]{10,}$/.test(form.phone)) {
      enqueueSnackbar("Please enter a valid phone number", { variant: "warning" });
      return false;
    }
    if (form.service_types.length === 0) {
      enqueueSnackbar("At least one service type must be selected", { variant: "warning" });
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      const payload = {
        ...form,
        vendorCode: form.vendorCode.trim() || '',
        name: form.name.trim(),
        contact_person: form.contact_person.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        notes: form.notes.trim()
      };

      if (editingId) {
        await updateLinenVendor(editingId, payload);
        enqueueSnackbar("🎉 Vendor updated successfully", { variant: "success" });
      } else {
        await createLinenVendor(payload);
        enqueueSnackbar("🎉 Vendor created successfully", { variant: "success" });
      }

      setOpen(false);
      loadVendors(page);
      loadActiveVendors();
    } catch (err) {
      console.error("Save error:", err);
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.errors?.join(", ") ||
        "Save failed";
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;

    try {
      await deleteLinenVendor(id);
      enqueueSnackbar("🗑️ Vendor deleted successfully", { variant: "success" });
      const shouldGoBack = vendors.length === 1 && page > 1;
      loadVendors(shouldGoBack ? page - 1 : page);
      loadActiveVendors();
    } catch (err) {
      console.error("Delete error:", err);
      const errorMessage = err.response?.data?.message || "Delete failed";
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  const handleStatusChange = async (vendorId, newStatus) => {
    try {
      await updateVendorStatus(vendorId, { status: newStatus });
      enqueueSnackbar(`Vendor ${newStatus} successfully`, { variant: "success" });
      loadVendors(page);
      loadActiveVendors();
    } catch (err) {
      console.error("Status change error:", err);
      enqueueSnackbar("Failed to update vendor status", { variant: "error" });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active": return "success";
      case "inactive": return "default";
      case "suspended": return "error";
      default: return "default";
    }
  };

  const renderStars = (rating) => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <Star 
            key={star} 
            sx={{ 
              color: rating >= star ? '#ffc107' : '#e0e0e0', 
              fontSize: 24,
              mr: 0.5
            }} 
          />
        ))}
        <Typography variant="body1" sx={{ ml: 1, fontSize: '1.1rem', fontWeight: 600 }}>
          {rating}.0
        </Typography>
      </Box>
    );
  };

  // Calculate statistics
  const calculateStats = () => {
    const activeCount = vendors.filter(v => v.status === 'active').length;
    const inactiveCount = vendors.filter(v => v.status === 'inactive').length;
    const suspendedCount = vendors.filter(v => v.status === 'suspended').length;
    const pickupCount = vendors.filter(v => v.pickup_available).length;
    
    return {
      activeCount,
      inactiveCount,
      suspendedCount,
      pickupCount,
      totalVendors: vendors.length
    };
  };

  const stats = calculateStats();

  return (
    <Box sx={{ p: 4, backgroundColor: 'grey.50', minHeight: '100vh' }}>
      {/* Header Section */}
      <Card sx={{ 
        mb: 4, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        borderRadius: 3
      }}>
        <CardContent sx={{ p: 4 }}>
          <Grid container alignItems="center" spacing={3}>
            <Grid item>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <LocalShipping sx={{ fontSize: 48, color: 'white' }} />
              </Avatar>
            </Grid>
            <Grid item xs>
              <Typography 
                variant="h2" 
                fontWeight={800}
                sx={{ 
                  color: 'white',
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  mb: 1
                }}
              >
                Linen Vendor Master
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'white', 
                  opacity: 0.9,
                  fontSize: '1.3rem'
                }}
              >
                Manage laundry agencies and service providers
              </Typography>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                startIcon={<Add sx={{ fontSize: 28 }} />}
                onClick={handleOpenAdd}
                sx={{
                  backgroundColor: 'white',
                  color: 'primary.main',
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  '&:hover': {
                    backgroundColor: 'grey.100',
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Add Vendor
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Avatar sx={{ backgroundColor: 'success.light', mb: 2, mx: 'auto' }}>
                <CheckCircle sx={{ fontSize: 32, color: 'success.main' }} />
              </Avatar>
              <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 800, color: 'success.main', mb: 1 }}>
                {stats.activeCount}
              </Typography>
              <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                Active Vendors
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Avatar sx={{ backgroundColor: 'warning.light', mb: 2, mx: 'auto' }}>
                <Warning sx={{ fontSize: 32, color: 'warning.main' }} />
              </Avatar>
              <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 800, color: 'warning.main', mb: 1 }}>
                {stats.inactiveCount}
              </Typography>
              <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                Inactive
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Avatar sx={{ backgroundColor: 'error.light', mb: 2, mx: 'auto' }}>
                <Cancel sx={{ fontSize: 32, color: 'error.main' }} />
              </Avatar>
              <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 800, color: 'error.main', mb: 1 }}>
                {stats.suspendedCount}
              </Typography>
              <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                Suspended
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Avatar sx={{ backgroundColor: 'primary.light', mb: 2, mx: 'auto' }}>
                <LocalShipping sx={{ fontSize: 32, color: 'primary.main' }} />
              </Avatar>
              <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 800, color: 'primary.main', mb: 1 }}>
                {stats.pickupCount}
              </Typography>
              <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                Pickup Service
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
        <CardHeader
          title={
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: '1.6rem' }}>
              🏢 Vendor Management
            </Typography>
          }
          subheader={
            <Typography sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
              Manage laundry service providers, track performance, and monitor service quality
            </Typography>
          }
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'grey.50'
          }}
        />
        
        <CardContent>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab 
                icon={<Business sx={{ fontSize: 24 }} />} 
                iconPosition="start"
                label={
                  <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                    All Vendors ({totalCount})
                  </Typography>
                }
              />
              <Tab 
                icon={<LocalShipping sx={{ fontSize: 24 }} />} 
                iconPosition="start"
                label={
                  <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                    Active Vendors ({activeVendors.length})
                  </Typography>
                }
              />
            </Tabs>
          </Box>

          {/* Search and Filters Section */}
          <Grid container spacing={3} alignItems="center" sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by name, code, contact..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "primary.main", fontSize: 28 }} />
                    </InputAdornment>
                  ),
                  sx: {
                    fontSize: '1.1rem',
                    '& input': {
                      padding: '12px 14px',
                      fontSize: '1.1rem'
                    }
                  }
                }}
                size="medium"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="medium">
                <InputLabel sx={{ fontSize: '1.1rem' }}>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                  sx={{ fontSize: '1.1rem' }}
                >
                  <MenuItem value="active" sx={{ fontSize: '1.1rem' }}>Active</MenuItem>
                  <MenuItem value="inactive" sx={{ fontSize: '1.1rem' }}>Inactive</MenuItem>
                  <MenuItem value="suspended" sx={{ fontSize: '1.1rem' }}>Suspended</MenuItem>
                  <MenuItem value="" sx={{ fontSize: '1.1rem' }}>All Status</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="medium">
                <InputLabel sx={{ fontSize: '1.1rem' }}>Service Type</InputLabel>
                <Select
                  value={serviceTypeFilter}
                  onChange={(e) => setServiceTypeFilter(e.target.value)}
                  label="Service Type"
                  sx={{ fontSize: '1.1rem' }}
                >
                  <MenuItem value="" sx={{ fontSize: '1.1rem' }}>All Services</MenuItem>
                  {serviceTypes.map((service) => (
                    <MenuItem key={service} value={service} sx={{ fontSize: '1.1rem' }}>
                      {service.replace('_', ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="medium">
                <InputLabel sx={{ fontSize: '1.1rem' }}>Pickup</InputLabel>
                <Select
                  value={pickupFilter}
                  onChange={(e) => setPickupFilter(e.target.value)}
                  label="Pickup"
                  sx={{ fontSize: '1.1rem' }}
                >
                  <MenuItem value="" sx={{ fontSize: '1.1rem' }}>All</MenuItem>
                  <MenuItem value="true" sx={{ fontSize: '1.1rem' }}>Available</MenuItem>
                  <MenuItem value="false" sx={{ fontSize: '1.1rem' }}>Not Available</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Button 
                startIcon={<Add sx={{ fontSize: 28 }} />} 
                variant="contained" 
                onClick={handleOpenAdd}
                sx={{ 
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  minWidth: 160
                }}
              >
                Add Vendor
              </Button>
            </Grid>
          </Grid>

          {/* Error Alert */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                fontSize: '1.1rem',
                borderRadius: 3,
                '& .MuiAlert-message': { fontSize: '1.1rem' }
              }}
            >
              {error}
            </Alert>
          )}

          {/* Tab Panels */}
          <TabPanel value={tabValue} index={0}>
            {/* All Vendors */}
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8, flexDirection: 'column', gap: 2 }}>
                <CircularProgress size={60} thickness={4} />
                <Typography variant="h4" sx={{ fontSize: '1.8rem', fontWeight: 600 }}>
                  Loading Vendors...
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                  Preparing vendor information
                </Typography>
              </Box>
            ) : (
              <>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary', mb: 3 }}>
                  📊 Showing {vendors.length} of {totalCount} vendors
                </Typography>

                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: 'primary.main' }}>
                      <TableRow>
                        <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          🏷️ Code
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          🏢 Vendor Name
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          👤 Contact
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          📞 Phone / Email
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          🛠️ Services
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          ⭐ Rating
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          🚚 Pickup/Delivery
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          📊 Status
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          🛠️ Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {vendors.length > 0 ? (
                        vendors.map((vendor) => (
                          <TableRow 
                            key={vendor._id} 
                            hover
                            sx={{ 
                              '&:last-child td, &:last-child th': { border: 0 },
                              '&:hover': {
                                backgroundColor: 'primary.light',
                                transform: 'translateY(-1px)',
                                transition: 'all 0.3s ease'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <TableCell>
                              <Typography variant="body1" fontWeight={700} sx={{ fontSize: '1.1rem' }}>
                                {vendor.vendorCode}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body1" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
                                {vendor.name}
                              </Typography>
                              {vendor.notes && (
                                <Typography variant="body2" color="text.secondary" display="block" sx={{ fontSize: '1rem' }}>
                                  {vendor.notes}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                                {vendor.contact_person || "-"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Phone sx={{ fontSize: 18, color: 'primary.main', mr: 1 }} />
                                <Typography variant="body2" sx={{ fontSize: '1.1rem' }}>
                                  {vendor.phone || "-"}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Email sx={{ fontSize: 18, color: 'primary.main', mr: 1 }} />
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                  {vendor.email || "-"}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Stack direction="column" spacing={1}>
                                {vendor.service_types?.map(service => (
                                  <Chip
                                    key={service}
                                    label={service.replace('_', ' ')}
                                    size="medium"
                                    variant="outlined"
                                    sx={{ fontSize: '1rem', fontWeight: 600 }}
                                  />
                                ))}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              {renderStars(vendor.rating || 3)}
                            </TableCell>
                            <TableCell>
                              <Stack direction="column" spacing={1}>
                                {vendor.pickup_available && (
                                  <Chip 
                                    label="Pickup Available" 
                                    size="medium" 
                                    color="primary" 
                                    variant="outlined"
                                    sx={{ fontSize: '1rem', fontWeight: 600 }}
                                  />
                                )}
                                {vendor.delivery_available && (
                                  <Chip 
                                    label="Delivery Available" 
                                    size="medium" 
                                    color="secondary" 
                                    variant="outlined"
                                    sx={{ fontSize: '1rem', fontWeight: 600 }}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={vendor.status}
                                color={getStatusColor(vendor.status)}
                                size="medium"
                                sx={{ fontSize: '1rem', fontWeight: 600, minWidth: 100 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <IconButton
                                  size="large"
                                  onClick={() => handleEdit(vendor)}
                                  sx={{ 
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    '&:hover': { backgroundColor: 'primary.dark' }
                                  }}
                                >
                                  <Edit sx={{ fontSize: 20 }} />
                                </IconButton>
                                <IconButton
                                  size="large"
                                  onClick={() => handleDelete(vendor._id, vendor.name)}
                                  sx={{ 
                                    backgroundColor: 'error.main',
                                    color: 'white',
                                    '&:hover': { backgroundColor: 'error.dark' }
                                  }}
                                >
                                  <Delete sx={{ fontSize: 20 }} />
                                </IconButton>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                            <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                              <Business sx={{ fontSize: 80, mb: 2, opacity: 0.3 }} />
                              <Typography variant="h4" sx={{ fontSize: '1.8rem', mb: 1, fontWeight: 600 }}>
                                No Vendors Found
                              </Typography>
                              <Typography variant="h6" sx={{ fontSize: '1.2rem', mb: 3 }}>
                                {search ? "No vendors found matching your criteria" : "Get started by adding your first vendor"}
                              </Typography>
                              <Button 
                                variant="contained" 
                                startIcon={<Add sx={{ fontSize: 28 }} />}
                                onClick={handleOpenAdd}
                                sx={{ 
                                  fontSize: '1.2rem',
                                  px: 4,
                                  py: 1.5,
                                  borderRadius: 3
                                }}
                              >
                                Add First Vendor
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination 
                      count={totalPages} 
                      page={page} 
                      onChange={(e, value) => loadVendors(value)}
                      color="primary"
                      size="large"
                      showFirstButton
                      showLastButton
                      sx={{
                        '& .MuiPaginationItem-root': {
                          fontSize: '1.1rem',
                          fontWeight: 600
                        }
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {/* Active Vendors */}
            <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary', mb: 3 }}>
              ✅ Currently active service providers
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
              <Table>
                <TableHead sx={{ backgroundColor: 'success.main' }}>
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      🏢 Vendor Name
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      👤 Contact
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      🛠️ Services
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      ⏱️ Turnaround
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      📦 Capacity/Day
                    </TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      🛠️ Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeVendors.length > 0 ? (
                    activeVendors.map((vendor) => (
                      <TableRow 
                        key={vendor._id} 
                        hover
                        sx={{ 
                          '&:hover': {
                            backgroundColor: 'success.light',
                            transform: 'translateY(-1px)',
                            transition: 'all 0.3s ease'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <TableCell>
                          <Typography variant="body1" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
                            {vendor.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                            {vendor.vendorCode}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Phone sx={{ fontSize: 18, color: 'primary.main', mr: 1 }} />
                            <Typography variant="body2" sx={{ fontSize: '1.1rem' }}>
                              {vendor.phone || "-"}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Email sx={{ fontSize: 18, color: 'primary.main', mr: 1 }} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                              {vendor.email || "-"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {vendor.service_types?.map(service => (
                              <Chip
                                key={service}
                                label={service.replace('_', ' ')}
                                size="medium"
                                variant="outlined"
                                sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5 }}
                              />
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Schedule sx={{ fontSize: 24, mr: 1, color: 'primary.main' }} />
                            <Typography variant="body1" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
                              {vendor.turnaround_time}h
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
                            {vendor.capacity_per_day} items
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="large"
                            onClick={() => handleEdit(vendor)}
                            sx={{ 
                              backgroundColor: 'primary.main',
                              color: 'white',
                              '&:hover': { backgroundColor: 'primary.dark' }
                            }}
                          >
                            <Edit sx={{ fontSize: 20 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                          <LocalShipping sx={{ fontSize: 80, mb: 2, opacity: 0.3 }} />
                          <Typography variant="h4" sx={{ fontSize: '1.8rem', mb: 1, fontWeight: 600 }}>
                            No Active Vendors
                          </Typography>
                          <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                            Add vendors and set their status to active to see them here.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={open} 
        onClose={() => !saving && setOpen(false)} 
        maxWidth="md" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            boxShadow: '0 16px 48px rgba(0,0,0,0.2)'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: 'primary.main', 
          color: 'white',
          py: 3
        }}>
          <Typography variant="h3" sx={{ fontSize: '1.8rem', fontWeight: 700 }}>
            {editingId ? "✏️ Edit Vendor" : "➕ Add New Vendor"}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Vendor Code"
                  value={form.vendorCode}
                  onChange={(e) => setForm({ ...form, vendorCode: e.target.value })}
                  fullWidth
                  size="medium"
                  helperText="Leave blank to auto-generate"
                  InputProps={{
                    sx: { fontSize: '1.1rem' }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: '1.1rem' }
                  }}
                  FormHelperTextProps={{
                    sx: { fontSize: '1rem' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Vendor Name *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  fullWidth
                  required
                  size="medium"
                  error={!form.name.trim()}
                  helperText={!form.name.trim() ? "Name is required" : ""}
                  InputProps={{
                    sx: { fontSize: '1.1rem' }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: '1.1rem' }
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Contact Person"
                  value={form.contact_person}
                  onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                  fullWidth
                  size="medium"
                  InputProps={{
                    sx: { fontSize: '1.1rem' }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: '1.1rem' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  fullWidth
                  size="medium"
                  error={form.phone && !/^[0-9+\-\s()]{10,}$/.test(form.phone)}
                  helperText={form.phone && !/^[0-9+\-\s()]{10,}$/.test(form.phone) ? "Invalid phone format" : ""}
                  InputProps={{
                    sx: { fontSize: '1.1rem' }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: '1.1rem' }
                  }}
                />
              </Grid>
            </Grid>

            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              fullWidth
              size="medium"
              error={form.email && !/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(form.email)}
              helperText={form.email && !/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(form.email) ? "Invalid email format" : ""}
              InputProps={{
                sx: { fontSize: '1.1rem' }
              }}
              InputLabelProps={{
                sx: { fontSize: '1.1rem' }
              }}
            />

            <TextField
              label="Address"
              multiline
              rows={2}
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              fullWidth
              size="medium"
              InputProps={{
                sx: { fontSize: '1.1rem' }
              }}
              InputLabelProps={{
                sx: { fontSize: '1.1rem' }
              }}
            />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="medium">
                  <InputLabel sx={{ fontSize: '1.1rem' }}>Rating</InputLabel>
                  <Select
                    value={form.rating}
                    onChange={(e) => setForm({ ...form, rating: e.target.value })}
                    label="Rating"
                    sx={{ fontSize: '1.1rem' }}
                  >
                    {[1, 2, 3, 4, 5].map(rating => (
                      <MenuItem key={rating} value={rating} sx={{ fontSize: '1.1rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {renderStars(rating)}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Turnaround Time (hours)"
                  type="number"
                  value={form.turnaround_time}
                  onChange={(e) => setForm({ ...form, turnaround_time: Math.max(1, Number(e.target.value)) })}
                  fullWidth
                  size="medium"
                  inputProps={{ 
                    min: 1,
                    style: { fontSize: '1.1rem' }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: '1.1rem' }
                  }}
                />
              </Grid>
            </Grid>

            <TextField
              label="Daily Capacity"
              type="number"
              value={form.capacity_per_day}
              onChange={(e) => setForm({ ...form, capacity_per_day: Math.max(1, Number(e.target.value)) })}
              fullWidth
              size="medium"
              inputProps={{ 
                min: 1,
                style: { fontSize: '1.1rem' }
              }}
              InputLabelProps={{
                sx: { fontSize: '1.1rem' }
              }}
              helperText="Maximum items they can process per day"
              FormHelperTextProps={{
                sx: { fontSize: '1rem' }
              }}
            />

            <FormControl component="fieldset" variant="standard">
              <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 600, mb: 2 }}>
                Service Types *
              </Typography>
              <FormGroup row sx={{ gap: 2 }}>
                {serviceTypes.map((service) => (
                  <FormControlLabel
                    key={service}
                    control={
                      <Checkbox
                        checked={form.service_types.includes(service)}
                        onChange={() => handleServiceTypeChange(service)}
                        name={service}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '1.1rem' }}>
                        {service.replace('_', ' ')}
                      </Typography>
                    }
                  />
                ))}
              </FormGroup>
            </FormControl>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.pickup_available}
                      onChange={(e) => setForm({ ...form, pickup_available: e.target.checked })}
                      sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                      Pickup Available
                    </Typography>
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.delivery_available}
                      onChange={(e) => setForm({ ...form, delivery_available: e.target.checked })}
                      sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                      Delivery Available
                    </Typography>
                  }
                />
              </Grid>
            </Grid>

            {editingId && (
              <FormControl fullWidth size="medium">
                <InputLabel sx={{ fontSize: '1.1rem' }}>Status</InputLabel>
                <Select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  label="Status"
                  sx={{ fontSize: '1.1rem' }}
                >
                  <MenuItem value="active" sx={{ fontSize: '1.1rem' }}>Active</MenuItem>
                  <MenuItem value="inactive" sx={{ fontSize: '1.1rem' }}>Inactive</MenuItem>
                  <MenuItem value="suspended" sx={{ fontSize: '1.1rem' }}>Suspended</MenuItem>
                </Select>
              </FormControl>
            )}

            <TextField
              label="Notes"
              multiline
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              fullWidth
              size="medium"
              InputProps={{
                sx: { fontSize: '1.1rem' }
              }}
              InputLabelProps={{
                sx: { fontSize: '1.1rem' }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setOpen(false)} 
            disabled={saving}
            size="large"
            sx={{ 
              fontSize: '1.1rem',
              px: 4,
              py: 1
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave} 
            disabled={saving || !form.name.trim() || form.service_types.length === 0}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
            size="large"
            sx={{ 
              fontSize: '1.1rem',
              fontWeight: 600,
              px: 4,
              py: 1,
              minWidth: 140
            }}
          >
            {saving ? 'Saving...' : (editingId ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};